import {
  Component,
  OnInit,
  ViewEncapsulation,
  HostBinding,
  AfterContentInit,
  Renderer2,
  Input,
  ElementRef,
  ContentChildren,
  Inject,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  AfterViewInit,
  Output,
} from '@angular/core';
import { SubscriptMessage } from './subscript-message';
import { QueryList, EventEmitter } from '@angular/core';
import { PgFieldControl } from '../pg-field-control';
import { PgFieldComponent } from '../pg-field.component';

import { PgErrorDirective } from './pg-error.directive';
import { debounceTime, map, switchMap } from 'rxjs/operators';

import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Subscription, Observable, of } from 'rxjs';
import {
  PG_SUBSCRIPT_STATE_CONTROLLER_TOKEN,
  PgSubscriptController,
} from './pg-subscript-controller';
import { SubscriptPushOption } from './pg-subscript-push-option';

import {
  PgSubscriptRenderer,
  PG_SUBSCRIPT_RENDERER_TOKEN,
} from './pg-subscript-renderer';
import { AnimationService } from '../../core/ui/animation.service';
import { styleVariables } from '../../styles/variables';
import { PromiseQueue } from '../../core/async/promise-queue';

@Component({
  selector: 'pg-subscript-messages, grit-subscript-messages',
  templateUrl: './pg-subscript-messages.component.html',
  styleUrls: ['./pg-subscript-messages.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [PromiseQueue],
})
export class PgSubscriptMessagesComponent implements AfterViewInit {
  // Track the display state of the previous iteration
  public previousSubscriptDisplayState: {
    isVisible: boolean;
    elementRef: ElementRef;
  }[];

  largeViewport = false;
  breakpointSubscription: Subscription;

  @HostBinding('class')
  readonly _class = 'pg-subscript-messages';

  @ViewChild('contentTemplate', { static: true }) contentTemplate: TemplateRef<
    any
  >;
  @ViewChild('contentOutlet', { read: ViewContainerRef, static: true })
  contentOutlet: ViewContainerRef;
  @Output('messageChanges') messageChanges: EventEmitter<
    any
  > = new EventEmitter();

  @Input()
  pushOption: SubscriptPushOption = SubscriptPushOption.MULTI_LINE_RESPONSIVE;

  // Can override the hint display state
  // Null resets the control to the component
  // true or false shows or hides the hint
  // except in the case of error, in which case
  // an error message will override this.
  @Input()
  hintShowOverride: boolean | null = null;

  @ContentChildren(SubscriptMessage)
  _subscriptChildren: QueryList<SubscriptMessage>;

  get errorChildren(): PgErrorDirective[] {
    if (!this._subscriptChildren) {
      return [];
    }
    return this._subscriptChildren.filter(
      sc => sc.type === 'error'
    ) as PgErrorDirective[];
  }

  get hintChild() {
    if (!this._subscriptChildren) {
      return null;
    }
    return this._subscriptChildren.find(sc => sc.type === 'hint');
  }

  public control: PgFieldControl<any>;

  public get hostElement(): HTMLElement {
    return this._elementRef.nativeElement;
  }

  constructor(
    protected pgFieldParent: PgFieldComponent,
    public animation: AnimationService,
    public renderer: Renderer2,
    private _elementRef: ElementRef,
    private _breakpointObserver: BreakpointObserver,
    @Inject(PG_SUBSCRIPT_STATE_CONTROLLER_TOKEN)
    private subscriptController: PgSubscriptController,
    @Inject(PG_SUBSCRIPT_RENDERER_TOKEN)
    private subscriptRenderer: PgSubscriptRenderer,
    private _promiseQueue: PromiseQueue
  ) {}

  ngAfterViewInit() {
    this.control = this.pgFieldParent.control;
    const delay = 100;

    this.subscriptRenderer.renderMessages(this);
    setTimeout(() => {
      this.subscriptController.checkSubscriptState(this, true);
    }, 100);
    this.control.stateChanges.pipe(debounceTime(delay)).subscribe(() => {
      this._promiseQueue.queuePromise(() => {
        return this.subscriptController.checkSubscriptState(this, true);
      });
    });

    this._subscriptChildren.changes.pipe(debounceTime(delay)).subscribe(() => {
      this._promiseQueue.queuePromise(() => {
        this.subscriptController.checkSubscriptState(this, false);
      });
    });

    this.subscriptController.shownSubscriptMessage$;

    this.breakpointSubscription = this._breakpointObserver
      .observe(`(min-width: ${styleVariables.widthBreakpointMd}px)`)
      .subscribe((state: BreakpointState) => {
        this.largeViewport = state.matches;
        this._promiseQueue.queuePromise(() =>
          this.subscriptController.checkSubscriptState(this, true)
        );
      });
  }

  getSubscriptMessageType(): 'error' | 'hint' | 'none' {
    if (this.control.isError) {
      return 'error';
    }
    if (
      this.control.isEmpty &&
      !this.control.isFocused &&
      !this.hintShowOverride
    ) {
      return 'none';
    } else if (this.control.isDisabled && !this.control.isEmpty) {
      return 'none';
    } else {
      return 'hint';
    }
  }
}
